import { AnimatePresence, motion } from 'framer-motion';
import {
    Check,
    ChevronDown,
    ChevronRight,
    HelpCircle,
    Mic,
    RotateCcw,
    Square,
    Trash2,
    X
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const MAX_DURATION_SEC = 4 * 60; // 4 minutes

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// GUIDANCE — messages contextuels selon l'état global
// ─────────────────────────────────────────────────────────────────────────────

const getGuidanceState = (sections, recording) => {
  const filled = sections.filter((s) => s.text.trim().length > 20);
  const total = sections.length;

  if (filled.length === 0)
    return {
      key: 'start',
      message: 'Commencez par développer vos arguments dans les sections ci-dessous. Cliquez sur une section pour l\'ouvrir.',
      color: 'cyan',
    };
  if (filled.length < Math.ceil(total / 2))
    return {
      key: 'partial',
      message: `Bonne progression. Continuez à développer vos arguments — ${total - filled.length} section${total - filled.length > 1 ? 's' : ''} encore à rédiger.`,
      color: 'cyan',
    };
  if (filled.length < total)
    return {
      key: 'almost',
      message: `Presque prêt. Encore ${total - filled.length} section${total - filled.length > 1 ? 's' : ''}, puis vous pourrez passer à l'enregistrement.`,
      color: 'amber',
    };
  if (!recording.blob)
    return {
      key: 'ready',
      message: 'Tous vos arguments sont rédigés. Passez maintenant à l\'enregistrement pour défendre votre position à l\'oral.',
      color: 'emerald',
    };
  return {
    key: 'done',
    message: 'Arguments rédigés et enregistrement effectué. Votre contribution est complète.',
    color: 'emerald',
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// GUIDANCE BANNER
// ─────────────────────────────────────────────────────────────────────────────

const GuidanceBanner = ({ state }) => {
  const [dismissed, setDismissed] = useState(false);
  const [prevKey, setPrevKey] = useState(state.key);

  if (state.key !== prevKey) {
    setDismissed(false);
    setPrevKey(state.key);
  }

  if (dismissed) return null;

  const colorMap = {
    cyan:    'border-cyan-500/25 bg-cyan-500/[0.06] text-cyan-300/80 dot-cyan',
    amber:   'border-amber-500/25 bg-amber-500/[0.06] text-amber-300/80 dot-amber',
    emerald: 'border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-300/80 dot-emerald',
  };

  const dotColor = {
    cyan: 'bg-cyan-400 animate-pulse',
    amber: 'bg-amber-400 animate-pulse',
    emerald: 'bg-emerald-400',
  };

  const c = colorMap[state.color] || colorMap.cyan;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={state.key}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2 }}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border mb-5 ${c}`}
      >
        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor[state.color]}`} />
        <p className="flex-1 text-xs leading-relaxed">{state.message}</p>
        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
        >
          <X size={11} className="text-white/25" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ARGUMENT SECTION — une section de l'argumentation
// ─────────────────────────────────────────────────────────────────────────────

const ArgumentSection = ({ section, index, isOpen, onToggle, onTextChange }) => {
  const textareaRef = useRef(null);
  const wordCount = section.text.trim().split(/\s+/).filter(Boolean).length;
  const charCount = section.text.trim().length;
  const isFilled = charCount > 20;
  const isRich = wordCount >= 30;

  // Auto-focus textarea when opened
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [section.text, isOpen]);

  return (
    <motion.div
      layout
      className={`rounded-xl border transition-all duration-200 overflow-hidden ${
        isOpen
          ? 'border-cyan-500/40 bg-cyan-500/[0.03] shadow-lg shadow-cyan-500/5'
          : isRich
          ? 'border-emerald-500/20 bg-emerald-500/[0.02]'
          : isFilled
          ? 'border-white/[0.10] bg-white/[0.02]'
          : 'border-white/[0.06] bg-transparent hover:border-white/[0.10] hover:bg-white/[0.015]'
      }`}
    >
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        {/* Index / status */}
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-bold transition-all ${
            isRich
              ? 'bg-emerald-500 text-black'
              : isFilled
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40'
              : isOpen
              ? 'bg-white/10 text-white/60 border border-white/15'
              : 'bg-white/[0.04] text-slate-600 border border-white/[0.06]'
          }`}
        >
          {isRich ? <Check size={12} /> : index + 1}
        </div>

        {/* Label + preview */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium transition-colors ${
            isOpen ? 'text-white' : isFilled ? 'text-white/80' : 'text-slate-400'
          }`}>
            {section.label}
          </p>
          {!isOpen && section.text.trim() && (
            <p className="text-[11px] text-slate-600 mt-0.5 truncate leading-relaxed">
              {section.text.trim().slice(0, 80)}{section.text.trim().length > 80 ? '…' : ''}
            </p>
          )}
        </div>

        {/* Word count + chevron */}
        <div className="flex items-center gap-2.5 shrink-0">
          {section.text.trim() && (
            <span className={`text-[10px] font-medium ${isRich ? 'text-emerald-400' : 'text-slate-600'}`}>
              {wordCount} mot{wordCount > 1 ? 's' : ''}
            </span>
          )}
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.15 }}>
            <ChevronDown size={14} className={isOpen ? 'text-cyan-400' : 'text-slate-600'} />
          </motion.div>
        </div>
      </button>

      {/* Expanded content */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div className="px-4 pb-4 pt-1 border-t border-white/[0.06]">
              {section.hint && (
                <p className="text-[11px] text-slate-500 italic mb-3 leading-relaxed">
                  {section.hint}
                </p>
              )}
              <textarea
                ref={textareaRef}
                value={section.text}
                onChange={(e) => onTextChange(e.target.value)}
                placeholder="Développez votre argument ici…"
                className="w-full px-3 py-2.5 text-sm rounded-xl resize-none outline-none transition-all min-h-[120px]
                  bg-black/30 border border-white/[0.08] focus:border-cyan-500/50 focus:bg-cyan-500/[0.02]
                  placeholder:text-slate-700 text-slate-200 leading-relaxed"
                style={{ overflow: 'hidden' }}
              />
              <div className="flex items-center justify-between mt-2 px-1">
                <span className="text-[10px] text-slate-700">
                  {charCount} caractère{charCount > 1 ? 's' : ''}
                </span>
                {charCount > 0 && charCount < 20 && (
                  <span className="text-[10px] text-amber-500/60">
                    Développez davantage pour valider
                  </span>
                )}
                {isRich && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-[10px] text-emerald-400 flex items-center gap-1"
                  >
                    <Check size={9} /> Argument développé
                  </motion.span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VOICE RECORDER — enregistrement audio avec aperçu des notes
// ─────────────────────────────────────────────────────────────────────────────

const VoiceRecorder = ({ sections, recording, onRecordingChange, isReadyToRecord }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [activeNoteIndex, setActiveNoteIndex] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        onRecordingChange({ blob, url, duration: elapsed });
        stream.getTracks().forEach((t) => t.stop());
      };

      mr.start(100);
      setIsRecording(true);
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev >= MAX_DURATION_SEC - 1) {
            stopRecording();
            return MAX_DURATION_SEC;
          }
          return prev + 1;
        });
      }, 1000);
    } catch {
      setPermissionDenied(true);
    }
  };

  const stopRecording = useCallback(() => {
    clearInterval(timerRef.current);
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  const handleDelete = () => {
    if (recording.url) URL.revokeObjectURL(recording.url);
    onRecordingChange({ blob: null, url: null, duration: 0 });
    setElapsed(0);
  };

  const progress = (elapsed / MAX_DURATION_SEC) * 100;
  const filledSections = sections.filter((s) => s.text.trim().length > 20);

  return (
    <div className="space-y-4">
      {/* Panel title */}
      <div className="flex items-center gap-2 mb-1">
        <Mic size={14} className="text-orange-400" />
        <h3 className="text-sm font-semibold text-white">Défense orale</h3>
      </div>

      {/* Notes rapides pendant l'enregistrement */}
      {filledSections.length > 0 && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              Vos notes — {filledSections.length} section{filledSections.length > 1 ? 's' : ''}
            </p>
            <div className="flex gap-1">
              {filledSections.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveNoteIndex(i)}
                  className={`w-5 h-1.5 rounded-full transition-all ${
                    i === activeNoteIndex ? 'bg-orange-400' : 'bg-white/10 hover:bg-white/20'
                  }`}
                />
              ))}
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeNoteIndex}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
              className="px-4 py-3"
            >
              <p className="text-[10px] font-semibold text-orange-400/70 mb-1.5">
                {filledSections[activeNoteIndex]?.label}
              </p>
              <p className="text-xs text-slate-400 leading-relaxed line-clamp-4">
                {filledSections[activeNoteIndex]?.text}
              </p>
            </motion.div>
          </AnimatePresence>
          {filledSections.length > 1 && (
            <div className="px-4 pb-3 flex justify-end">
              <button
                onClick={() => setActiveNoteIndex((i) => (i + 1) % filledSections.length)}
                className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors flex items-center gap-1"
              >
                Note suivante <ChevronRight size={10} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Permission denied */}
      {permissionDenied && (
        <div className="px-4 py-3 rounded-xl border border-red-500/30 bg-red-500/10">
          <p className="text-xs text-red-300 leading-relaxed">
            L'accès au microphone a été refusé. Autorisez l'accès dans les paramètres de votre navigateur pour enregistrer.
          </p>
        </div>
      )}

      {/* Recorder UI */}
      {!recording.url ? (
        <div className={`rounded-xl border p-5 space-y-4 transition-all ${
          isReadyToRecord
            ? 'border-orange-500/30 bg-orange-500/[0.05]'
            : 'border-white/[0.06] bg-white/[0.01]'
        }`}>
          {/* Status + timer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full transition-all ${
                isRecording ? 'bg-red-500 animate-pulse' : isReadyToRecord ? 'bg-orange-400/60' : 'bg-white/20'
              }`} />
              <span className="text-xs font-medium text-slate-400">
                {isRecording ? 'Enregistrement en cours' : isReadyToRecord ? 'Prêt à enregistrer' : 'Rédigez vos arguments d\'abord'}
              </span>
            </div>
            <span className={`text-sm font-mono font-semibold transition-colors ${
              isRecording ? 'text-red-400' : 'text-slate-600'
            }`}>
              {formatTime(elapsed)} / {formatTime(MAX_DURATION_SEC)}
            </span>
          </div>

          {/* Progress bar durée */}
          {isRecording && (
            <div className="h-0.5 rounded-full bg-white/[0.06] overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-red-500"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          )}

          {/* Waveform simulée — visible pendant l'enregistrement */}
          {isRecording && (
            <div className="flex items-center justify-center gap-0.5 h-10">
              {Array.from({ length: 28 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="w-1 rounded-full bg-red-500/60"
                  animate={{
                    height: [4, Math.random() * 28 + 4, 4],
                  }}
                  transition={{
                    duration: 0.4 + Math.random() * 0.4,
                    repeat: Infinity,
                    delay: i * 0.05,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
          )}

          {/* Bouton principal */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!isReadyToRecord && !isRecording}
            className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
              isRecording
                ? 'bg-red-500 hover:bg-red-400 text-white shadow-lg shadow-red-500/20'
                : isReadyToRecord
                ? 'bg-orange-500 hover:bg-orange-400 text-black shadow-lg shadow-orange-500/20'
                : 'bg-white/[0.04] border border-white/[0.06] text-slate-600 cursor-not-allowed'
            }`}
          >
            {isRecording ? (
              <><Square size={14} fill="currentColor" /> Arrêter l'enregistrement</>
            ) : (
              <><Mic size={14} /> {isReadyToRecord ? 'Démarrer l\'enregistrement' : 'Rédigez vos arguments d\'abord'}</>
            )}
          </button>

          {!isReadyToRecord && !isRecording && (
            <p className="text-[11px] text-slate-600 text-center">
              Vous pourrez enregistrer une fois vos arguments rédigés.
            </p>
          )}
        </div>
      ) : (
        /* Playback */
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5 space-y-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-xs font-medium text-emerald-300">
                Enregistrement — {formatTime(recording.duration)}
              </span>
            </div>
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-600 hover:text-red-400 transition-all"
              title="Supprimer et recommencer"
            >
              <Trash2 size={13} />
            </button>
          </div>

          <audio
            src={recording.url}
            controls
            className="w-full h-9 rounded-lg"
            style={{ accentColor: '#34d399' }}
          />

          <button
            onClick={() => {
              handleDelete();
              setTimeout(startRecording, 100);
            }}
            className="w-full py-2.5 rounded-xl text-xs font-semibold border border-white/10 bg-white/[0.03] text-slate-400 hover:text-white hover:border-white/20 transition-all flex items-center justify-center gap-2"
          >
            <RotateCcw size={12} /> Refaire l'enregistrement
          </button>
        </motion.div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HELP PANEL
// ─────────────────────────────────────────────────────────────────────────────

const HelpPanel = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-3 mb-5"
  >
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs font-semibold text-white">Comment utiliser cet outil</p>
      <button onClick={onClose} className="p-1 rounded hover:bg-white/10 transition-colors">
        <X size={12} className="text-slate-500" />
      </button>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {[
        { label: 'Étape 1 — Rédiger', desc: 'Ouvrez chaque section et développez votre argument. Un mot-count vous indique la richesse de votre texte.' },
        { label: 'Étape 2 — Enregistrer', desc: 'Une fois vos arguments rédigés, défendez-les à l\'oral. Vos notes restent visibles pendant l\'enregistrement.' },
        { label: 'Sections indépendantes', desc: 'Chaque section peut être rédigée dans n\'importe quel ordre. Revenez modifier à tout moment.' },
        { label: 'Refaire l\'enregistrement', desc: 'Si vous n\'êtes pas satisfait, supprimez et recommencez. Seul le dernier enregistrement est conservé.' },
      ].map((h, i) => (
        <div key={i} className="flex gap-2.5">
          <ChevronRight size={12} className="text-cyan-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-white">{h.label}</p>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{h.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// COMPLETION SUMMARY — récapitulatif final
// ─────────────────────────────────────────────────────────────────────────────

const CompletionSummary = ({ sections, recording }) => {
  const filledSections = sections.filter((s) => s.text.trim().length > 20);
  const totalWords = sections.reduce(
    (acc, s) => acc + s.text.trim().split(/\s+/).filter(Boolean).length,
    0
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 p-5 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] space-y-4"
    >
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-400" />
        <p className="text-sm font-semibold text-emerald-300">Contribution complète</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Sections rédigées', value: `${filledSections.length}/${sections.length}` },
          { label: 'Mots au total', value: totalWords },
          { label: 'Durée enregistrement', value: recording.duration ? formatTime(recording.duration) : '—' },
        ].map((stat, i) => (
          <div key={i} className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
            <p className="text-lg font-semibold text-white">{stat.value}</p>
            <p className="text-[10px] text-slate-600 uppercase tracking-wider mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * DualBuilder refondu — atelier argumentation texte + voix
 *
 * Props :
 *   config {
 *     title?: string
 *     sections: Array<{ label: string, hint?: string }>
 *     recordingRequired?: boolean   — si true, l'enregistrement est obligatoire
 *   }
 *   onComplete?: (data: { sections, recording }) => void
 */
const DualBuilder = ({ config = {}, onComplete }) => {
  const rawSections = config.sections || [
    { label: 'Votre thèse principale' },
    { label: 'Premiers arguments' },
    { label: 'Arguments secondaires' },
    { label: 'Conclusion et nuances' },
  ];

  const [sections, setSections] = useState(
    rawSections.map((s) => ({
      label: typeof s === 'string' ? s : s.label,
      hint: typeof s === 'string' ? null : (s.hint || null),
      text: '',
    }))
  );

  const [openIndex, setOpenIndex] = useState(0);
  const [recording, setRecording] = useState({ blob: null, url: null, duration: 0 });
  const [showHelp, setShowHelp] = useState(false);

  const filledSections = sections.filter((s) => s.text.trim().length > 20);
  const isReadyToRecord = filledSections.length >= Math.ceil(sections.length / 2);
  const isFullyComplete = filledSections.length === sections.length && recording.blob;

  const guidanceState = getGuidanceState(sections, recording);

  // Text change handler avec auto-avance vers section suivante
  const handleTextChange = useCallback(
    (index, value) => {
      setSections((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], text: value };
        return next;
      });
    },
    []
  );

  const handleToggle = (idx) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  // Progress global
  const totalWords = sections.reduce(
    (acc, s) => acc + s.text.trim().split(/\s+/).filter(Boolean).length,
    0
  );
  const progressPercent = Math.min(
    100,
    Math.round(
      ((filledSections.length / sections.length) * 70 + (recording.blob ? 30 : 0))
    )
  );

  return (
    <div className="w-full space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">
            {config.title || 'Atelier d\'argumentation'}
          </h2>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {sections.length} section{sections.length > 1 ? 's' : ''} · {totalWords} mot{totalWords !== 1 ? 's' : ''} rédigé{totalWords !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowHelp((v) => !v)}
          className={`p-2 rounded-xl border transition-all ${
            showHelp
              ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
              : 'border-white/10 bg-white/[0.03] text-slate-500 hover:text-white'
          }`}
          title="Aide"
        >
          <HelpCircle size={14} />
        </button>
      </div>

      {/* Help panel */}
      <AnimatePresence>
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      </AnimatePresence>

      {/* Guidance banner */}
      <GuidanceBanner state={guidanceState} />

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-600">Progression</span>
          <span className="text-[10px] font-semibold text-white">{progressPercent}%</span>
        </div>
        <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            className={`h-full rounded-full transition-colors ${
              progressPercent === 100 ? 'bg-emerald-500' : 'bg-cyan-500'
            }`}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Two-column layout on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* LEFT — Argument sections */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Vos arguments — {filledSections.length}/{sections.length}
            </span>
            <div className="flex-1 h-px bg-white/[0.06]" />
          </div>

          {sections.map((section, i) => (
            <ArgumentSection
              key={i}
              section={section}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => handleToggle(i)}
              onTextChange={(value) => handleTextChange(i, value)}
            />
          ))}
        </div>

        {/* RIGHT — Voice recorder */}
        <div className="lg:sticky lg:top-4 self-start">
          <VoiceRecorder
            sections={sections}
            recording={recording}
            onRecordingChange={setRecording}
            isReadyToRecord={isReadyToRecord}
          />
        </div>
      </div>

      {/* Completion summary */}
      {isFullyComplete && (
        <CompletionSummary sections={sections} recording={recording} />
      )}
    </div>
  );
};

export default DualBuilder;